import {z} from "zod/v4-mini"
import type {Subscription} from "rxjs"
import {defer, map} from "rxjs"
import {hashObject} from "@cm/utils"
import type {AnyJsonValue} from "#template/value-types"
import {anyJsonValue} from "#template/value-types"
import type {GeomBuilderContext, IndexingDim} from "#template/geometry-processing/geometry-graph"
import type {GeometryAttributeData, GeometryAttributeSetData, GeometryValueData} from "#template/nodes/geometry-graph/interfaces"
import type {ISceneManager} from "#template/interfaces/scene-manager"
import type {CurveObjectData, MeshObjectData, ObjectData} from "#template/interfaces/object-data"
import type {NumberLikeNode, StringLikeNode, BooleanLikeNode, JsonLikeNode, CurveLikeNode, MeshLikeNode, TransformLikeNode} from "#template/nodes/types"
import {numberLikeNode} from "#template/nodes/number/fundamental"
import {stringLikeNode} from "#template/nodes/string/fundamental"
import {booleanLikeNode} from "#template/nodes/boolean/fundamental"
import {jsonLikeNode} from "#template/nodes/json/fundamental"
import {curveLike} from "#template/nodes/object/curve/fundamental"
import {meshLike} from "#template/nodes/object/mesh/fundamental"
import {transformLikeNode} from "#template/nodes/transform/fundamental"
import {GeometryGraphNode} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {NodeClassImpl, NodeClass} from "#template/runtime-graph/types"
import {NotReady} from "#template/runtime-graph/slots"
import type {Inlet, Outlet} from "#template/runtime-graph/slots"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"

const TD = TypeDescriptors

const isCurveObjectData = (x: unknown): x is CurveObjectData => typeof x === "object" && x !== null && "type" in x && (x as {type: unknown}).type === "curve"
const isMeshObjectData = (x: unknown): x is MeshObjectData => typeof x === "object" && x !== null && "type" in x && (x as {type: unknown}).type === "mesh"

export const curveObjectData: z.ZodMiniType<CurveObjectData, CurveObjectData> = z.custom<CurveObjectData>(isCurveObjectData)
export const meshObjectData: z.ZodMiniType<MeshObjectData, MeshObjectData> = z.custom<MeshObjectData>(isMeshObjectData)

export type GeometryGeneratorArg = AnyJsonValue | GeometryAttributeData | GeometryAttributeSetData | GeometryValueData | CurveObjectData | MeshObjectData
export type GeometryGeneratorArgs = Record<string, GeometryGeneratorArg>

export type GeometryGeneratorCtxArg = {
    geomBuilderContext: GeomBuilderContext
    sceneManager: ISceneManager
}

export type GeometryGenerator<T extends GeometryAttributeData<IndexingDim> | GeometryAttributeSetData<IndexingDim> | GeometryValueData> = {
    fn: (ctx: GeometryGeneratorCtxArg, args: GeometryGeneratorArgs) => T
    hash: string
}

export const geometryGenerator = <T extends GeometryAttributeData<IndexingDim> | GeometryAttributeSetData<IndexingDim>>() =>
    z.custom<GeometryGenerator<T>>(
        (x) =>
            typeof x === "object" &&
            x !== null &&
            "fn" in x &&
            "hash" in x &&
            typeof (x as {fn: unknown}).fn === "function" &&
            typeof (x as {hash: unknown}).hash === "string",
    )

////////////////////////////////////////////////////////////////////////////////

export type GeometryGeneratorArgsWithHash = {
    args: GeometryGeneratorArgs
    hash: string
}

export const getHashForGeometryGeneratorArgs = (evaluatedArgs: GeometryGeneratorArgs) => {
    const objectDataFields = (objectData: ObjectData) => ({
        id: objectData.id,
        type: objectData.type,
        matrix: objectData.matrix.toArray(),
    })

    const completeDataFields = (data: {abstract?: {contentHash: string}; reified: {contentHash: string} | null}) => ({
        abstractContentHash: data.abstract?.contentHash,
        reifiedContentHash: data.reified?.contentHash,
    })

    const hashableValueForEvaluatedArg = (arg: GeometryGeneratorArg) => {
        if (isCurveObjectData(arg))
            return {
                objectData: objectDataFields(arg),
                completeCurveData: completeDataFields(arg.completeCurveData),
            }
        else if (isMeshObjectData(arg))
            return {
                objectData: objectDataFields(arg),
                completeMeshData: completeDataFields(arg.completeMeshData),
            }
        else return arg
    }

    return hashObject({
        type: "GeometryGeneratorArgsHash",
        evaluatedArgs: Object.entries(evaluatedArgs).map(([k, v]) => [k, hashableValueForEvaluatedArg(v)]),
    })
}

export const geometryGeneratorArgsWithHashTypeDescriptor = {
    deepCompare: (a: GeometryGeneratorArgsWithHash | null | undefined, b: GeometryGeneratorArgsWithHash | null | undefined) => {
        if (!a || !b) return a === b
        return a.hash === b.hash
    },
    deepCopy: (a: GeometryGeneratorArgsWithHash) => a,
}

export const generateGeometryGeneratorTypeDescriptor = <T extends GeometryAttributeData<IndexingDim> | GeometryAttributeSetData<IndexingDim>>() => ({
    deepCompare: (a: GeometryGenerator<T> | null | undefined, b: GeometryGenerator<T> | null | undefined) => {
        if (!a || !b) return a === b
        return a.hash === b.hash
    },
    deepCopy: (a: GeometryGenerator<T>) => a,
})

export type GeometryGeneratorOutputWithHash<T extends GeometryAttributeData<IndexingDim> | GeometryAttributeSetData<IndexingDim>> = {
    output: T
    hash: string
}

const getHashForGeometryGeneratorOutput = <T extends GeometryAttributeData<IndexingDim> | GeometryAttributeSetData<IndexingDim>>(
    generator: GeometryGenerator<T>,
    argsWithHash: GeometryGeneratorArgsWithHash,
) => hashObject({type: "GeometryGeneratorOutputHash", generatorHash: generator.hash, argsHash: argsWithHash.hash})

const generateGeometryGeneratorOutputTypeDescriptor = <T extends GeometryAttributeData<IndexingDim> | GeometryAttributeSetData<IndexingDim>>() => ({
    deepCompare: (a: GeometryGeneratorOutputWithHash<T> | null | undefined, b: GeometryGeneratorOutputWithHash<T> | null | undefined) => {
        if (!a || !b) return a === b
        return a.hash === b.hash
    },
    deepCopy: (a: GeometryGeneratorOutputWithHash<T>) => a,
})

// cached by `nodeClass` so every rebuild reuses one runtime-node class identity. The runtime `_node`
// guard (graph-builder-scope) keys on stable class identity; a fresh class per rebuild makes it misfire.
const geometryLambdaRuntimeNodeCache = new Map<string, unknown>()

export const generateGeometryLambdaRuntimeNode = <T extends GeometryAttributeData<IndexingDim> | GeometryAttributeSetData<IndexingDim>>(nodeClass: string) => {
    const cached = geometryLambdaRuntimeNodeCache.get(nodeClass)
    if (cached) return cached as typeof GeometryLambdaRuntimeNode

    const descriptor = {
        sceneManager: TD.inlet(TD.Identity<ISceneManager>()),
        geomBuilderContext: TD.inlet(TD.Identity<GeomBuilderContext>()),
        argsWithHash: TD.inlet(geometryGeneratorArgsWithHashTypeDescriptor),
        generator: TD.inlet(generateGeometryGeneratorTypeDescriptor<T>()),
        output: TD.outlet(generateGeometryGeneratorOutputTypeDescriptor<T>()),
    }

    class GeometryLambdaRuntimeNode implements NodeClassImpl<typeof descriptor, NodeClass<typeof descriptor>> {
        static descriptor = descriptor
        static uniqueName = nodeClass
        sceneManager!: Inlet<ISceneManager>
        geomBuilderContext!: Inlet<GeomBuilderContext>
        argsWithHash!: Inlet<GeometryGeneratorArgsWithHash>
        generator!: Inlet<GeometryGenerator<T>>
        output!: Outlet<GeometryGeneratorOutputWithHash<T>>

        pending: Subscription | null = null

        run() {
            this.output.emitIfChanged(NotReady)
            this.pending?.unsubscribe()

            if (this.sceneManager === NotReady || this.geomBuilderContext === NotReady || this.argsWithHash === NotReady || this.generator === NotReady) {
                return
            }

            const {sceneManager, geomBuilderContext, argsWithHash, generator} = this
            this.pending = sceneManager.addTask({
                description: `${nodeClass}(${this.argsWithHash.hash}:${this.generator.hash})`,
                task: defer(() => (async () => generator.fn({geomBuilderContext, sceneManager}, argsWithHash.args))()).pipe(
                    map((output) => {
                        this.pending = null
                        this.output.emitIfChanged({output: output, hash: getHashForGeometryGeneratorOutput(generator, argsWithHash)})
                    }),
                ),
                critical: true,
            })
        }
    }

    geometryLambdaRuntimeNodeCache.set(nodeClass, GeometryLambdaRuntimeNode)
    return GeometryLambdaRuntimeNode
}

////////////////////////////////////////////////////////////////////////////////

export const isGeometryGraphNode = (x: unknown): x is GeometryGraphNode<unknown> => x instanceof GeometryGraphNode
const geometryGraphNode = z.custom<GeometryGraphNode<unknown>>(isGeometryGraphNode)

export const evaluableGeometryGeneratorArg = z.union([
    anyJsonValue,
    numberLikeNode,
    stringLikeNode,
    booleanLikeNode,
    transformLikeNode,
    jsonLikeNode,
    geometryGraphNode,
    curveLike,
    meshLike,
])
export type EvaluableGeometryGeneratorArg =
    | AnyJsonValue
    | NumberLikeNode
    | StringLikeNode
    | BooleanLikeNode
    | TransformLikeNode
    | JsonLikeNode
    | GeometryGraphNode<unknown>
    | CurveLikeNode
    | MeshLikeNode
export const evaluableGeometryGeneratorArgs = z.record(z.string(), evaluableGeometryGeneratorArg)
export type EvaluableGeometryGeneratorArgs = Record<string, EvaluableGeometryGeneratorArg>
